import { useState, useReducer, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// State
import restaurantApi from '../../state/modules/restaurant/api';
import { selectUserCity } from '../../state/modules/city/actions';
import {
  fetchDeliveryRestaurantDetails,
  setLocalStorageCheckout,
  setCheckoutDeliveryAddress,
  resetCheckoutField,
} from '../../state/modules/order/actions';

// Utils
import { formatMinAddress } from '../../utils/helpers';
import { customScrollTo } from '../../utils/customScrollTo';

export default function useAddressSelector(onSuccessCallback) {
  const dispatch = useDispatch();
  const { userCity, list } = useSelector(state => state.city);
  const deliveryAddress = useSelector(state => state.order.checkout.deliveryAddress);

  const buttonRef = useRef(null);
  const [address, setAddress] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    deliveryAddress,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [addressInLineValue, setAddressInLineValue] = useState(address.address_in_line);
  const [isIntercomAndPorchOpened, setIsIntercomAndPorchOpened] = useState(
    Boolean(deliveryAddress.porch || deliveryAddress.intercom),
  );

  const isButtonExist = useMemo(() => Boolean(onSuccessCallback), [onSuccessCallback]);

  const isAddressNotConfirmed = useMemo(
    () => Boolean(address.address_in_line && address.address_in_line !== addressInLineValue),
    [address.address_in_line, addressInLineValue],
  );

  const isFlatMissing = useMemo(
    () => Boolean(addressInLineValue) && !String(address.flat ?? '').trim(),
    [addressInLineValue, address.flat],
  );

  const isError = useMemo(() => isAddressNotConfirmed || isFlatMissing, [
    isAddressNotConfirmed,
    isFlatMissing,
  ]);

  // вводим адрес
  const handleAddressOnChange = e => {
    setAddress({ [e.target.name]: e.target.value });
  };

  // открыть/закрыть домофон
  const handleIntercomAndPorchToggle = () => {
    setIsIntercomAndPorchOpened(prev => !prev);
  };

  // если у нас нет кнопки подтверждения, то менять адрес в стейте при каждом изменении поля
  const handleOnBlur = () => {
    if (isButtonExist) {
      return;
    }

    dispatch(setCheckoutDeliveryAddress(address));
  };
  const handleFlatChange = e => {
    const value = e.target.value;
    const next = { ...address, flat: value };

    setAddress({ flat: value }); // локальный стейт хука
    dispatch(setCheckoutDeliveryAddress(next)); // сразу кладём в Redux
  };
  const fetchRestaurantDetails = async (paramsAddress, savedAddressCityId) => {
    // ищем ближайший ресторан для этого адреса
    try {
      dispatch(
        setLocalStorageCheckout({ deliveryAddress: paramsAddress, deliveryType: 'delivery' }),
      );

      // если мы выбрали адрес из сохраненных, то он придет вторым аргументом в функцию
      let resultCityId = savedAddressCityId || userCity.id;
      if (!savedAddressCityId) {
        const { city_id } = await restaurantApi.getRestaurantAddress({
          address: paramsAddress.address_in_line,
        });

        resultCityId = city_id;
      }

      // если city_id найденного ресторана не совпадает с нашим текущим
      // то сделать редирект на нужный город
      if (resultCityId !== userCity.id) {
        const newCity = list.find(city => city.id === resultCityId);
        dispatch(selectUserCity(newCity));

        if (isButtonExist) {
          onSuccessCallback();
        }
      } else {
        setAddress(paramsAddress);
        setAddressInLineValue(paramsAddress.address_in_line);

        const onFetchDeliveryRestaurantDetailsSuccess = () => {
          dispatch(setCheckoutDeliveryAddress(paramsAddress));

          if (isButtonExist) {
            onSuccessCallback();
          }
        };

        // подтянуть данные о ресторане
        dispatch(
          fetchDeliveryRestaurantDetails(paramsAddress, onFetchDeliveryRestaurantDetailsSuccess),
        );
      }
    } catch (e) {
      console.warn(e);
    }
  };

  // когда выбрали адрес из предложки / на карте / стерли полностью
  const handleOnAddressInLineSelect = value => {
    const newAddress = { address_id: null, address_in_line: value };

    if (isButtonExist) {
      setAddress(newAddress);
      setAddressInLineValue(value);
      return;
    }

    // если нет кнопки подтвердить, то надо подтянуть новые данные ресторана
    if (value) {
      // если мы выбрали из предложки новый адрес, то подтянуть данные ресторана
      fetchRestaurantDetails(newAddress);
    } else {
      // если мы стерли поле адрес, то сбросить выбранный ресторан
      dispatch(setCheckoutDeliveryAddress({ address_id: null, address_in_line: '' }));
      dispatch(resetCheckoutField('deliveryRestaurant'));
      dispatch(resetCheckoutField('currentRestaurant'));
    }
  };

  // при нажатии на сохраненный адрес
  const handleSelectSavedAddress = selectedAddress => {
    const { id, flat, floor, porch, intercom } = selectedAddress;
    const newAddressInLine = formatMinAddress(selectedAddress);
    const newAddress = {
      address_id: id,
      address_in_line: newAddressInLine,
      flat,
      floor,
      porch,
      intercom,
    };

    // если есть кнопка подтвердить, то после нажатия подьехать к ней
    if (isButtonExist) {
      customScrollTo(buttonRef);
      setAddress(newAddress);
      setAddressInLineValue(newAddressInLine);
      return;
    }

    // если этой кнопки нет, значит надо подтянуть данные ресторана
    fetchRestaurantDetails(newAddress, selectedAddress.city_id);
  };

  // если нажали на кнопку ПОДТВЕРДИТЬ
  const handleOnAccept = async () => {
    // ищем ближайший ресторан для этого адреса
    setIsLoading(true);
    await fetchRestaurantDetails(address);
    setIsLoading(false);
  };

  // если мы откуда-то изменили deliveryAddress в стейте, то изменить и здесь
  useEffect(() => {
    if (deliveryAddress.address_in_line) {
      setAddress(deliveryAddress);
      setAddressInLineValue(deliveryAddress.address_in_line);
    }
  }, [deliveryAddress]);

  useEffect(() => {
    if (!isIntercomAndPorchOpened && (address.porch || address.intercom)) {
      setIsIntercomAndPorchOpened(true);
    }
  }, [address.porch, address.intercom]);

  return {
    buttonRef,
    address,
    isLoading,
    addressInLineValue,
    isIntercomAndPorchOpened,
    setAddressInLineValue,
    //
    handleIntercomAndPorchToggle,
    handleAddressOnChange,
    handleOnBlur,
    handleOnAddressInLineSelect,
    handleSelectSavedAddress,
    handleFlatChange,
    //
    isButtonExist,
    handleOnAccept,
    isError,
  };
}
